import { useEffect, useState } from "react";
import { Anchor, Button, Group, Modal, Select, Stack, TextInput, Textarea } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconArrowRight } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../../api/client";
import type { Customer, ReturnBox } from "../../api/types";

export function MoveToReturnBoxModal({
  opened,
  onClose,
  deviceIds,
  onMoved,
}: {
  opened: boolean;
  onClose: () => void;
  deviceIds: number[];
  onMoved: () => void;
}) {
  const { t } = useTranslation();
  const [boxes, setBoxes] = useState<ReturnBox[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [targetId, setTargetId] = useState<string | null>(null);
  const [newMode, setNewMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newForm, setNewForm] = useState({
    customer_id: null as string | null,
    received_at: new Date().toISOString().slice(0, 10),
    notes: "",
  });

  useEffect(() => {
    if (!opened) return;
    setTargetId(null);
    setNewMode(false);
    setNewForm({
      customer_id: null,
      received_at: new Date().toISOString().slice(0, 10),
      notes: "",
    });
    void (async () => {
      try {
        const [b, c] = await Promise.all([api.listReturnBoxes(), api.listCustomers()]);
        setBoxes(b);
        setCustomers(c);
      } catch (err) {
        const detail = err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
        notifications.show({ color: "red", message: detail.slice(0, 200) });
      }
    })();
  }, [opened]);

  async function submit() {
    if (deviceIds.length === 0) return;
    setSubmitting(true);
    try {
      let targetBoxId: number;
      let targetCode: string;
      if (newMode) {
        const created = await api.createReturnBox({
          customer_id: newForm.customer_id ? Number(newForm.customer_id) : null,
          received_at: newForm.received_at || null,
          notes: newForm.notes.trim() || null,
        });
        targetBoxId = created.id;
        targetCode = created.code;
      } else {
        if (!targetId) return;
        targetBoxId = Number(targetId);
        const found = boxes.find((b) => b.id === targetBoxId);
        targetCode = found?.code ?? `#${targetBoxId}`;
      }
      const res = await api.bulkAssignToReturnBox(targetBoxId, deviceIds);
      notifications.show({
        color: res.failed.length === 0 ? "green" : "yellow",
        message: t("rma.movedToast", {
          assigned: res.assigned.length,
          code: targetCode,
          failed: res.failed.length,
        }),
      });
      if (res.failed.length > 0) {
        for (const f of res.failed.slice(0, 3)) {
          notifications.show({ color: "red", message: `#${f.id}: ${f.error}` });
        }
      }
      onMoved();
      onClose();
    } catch (err) {
      const detail = err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
      notifications.show({ color: "red", title: t("rma.saveFailedTitle"), message: detail.slice(0, 200) });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t("rma.moveModalTitle")} centered>
      <Stack>
        {!newMode && (
          <>
            <Select
              label={t("rma.moveModalSelectLabel")}
              placeholder={t("rma.moveModalSelectPlaceholder")}
              data={boxes.map((b) => ({
                value: String(b.id),
                label: `${b.code}${b.customer_name ? " — " + b.customer_name : ""} (${b.total ?? 0})`,
              }))}
              value={targetId}
              onChange={setTargetId}
              searchable
              clearable
            />
            <Anchor component="button" type="button" onClick={() => setNewMode(true)}>
              {t("rma.moveModalNewBox")}
            </Anchor>
          </>
        )}
        {newMode && (
          <>
            <Select
              label={t("rma.moveModalNewBoxCustomer")}
              data={customers.map((c) => ({ value: String(c.id), label: c.name }))}
              value={newForm.customer_id}
              onChange={(v) => setNewForm({ ...newForm, customer_id: v })}
              searchable
              clearable
            />
            <TextInput
              type="date"
              label={t("rma.moveModalNewBoxDate")}
              value={newForm.received_at}
              onChange={(e) => setNewForm({ ...newForm, received_at: e.currentTarget.value })}
            />
            <Textarea
              label={t("rma.moveModalNewBoxNotes")}
              value={newForm.notes}
              onChange={(e) => setNewForm({ ...newForm, notes: e.currentTarget.value })}
              minRows={2}
            />
            <Anchor component="button" type="button" onClick={() => setNewMode(false)}>
              ← {t("common.cancel")}
            </Anchor>
          </>
        )}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            color="blue"
            loading={submitting}
            disabled={!newMode && !targetId}
            leftSection={<IconArrowRight size={16} />}
            onClick={() => void submit()}
          >
            {newMode ? t("rma.moveModalCreateAndMove") : t("rma.moveModalMove")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
